import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@motorway/mw-highway-code';

import { getAdStorageGranted } from 'Utilities/consent';
import { useTrustPilotWidgetLoader } from 'Utilities/hooks/useTrustPilotWidgetLoader';
import T from 'Utilities/texts';
import { TRUSTPILOT_URL } from 'Utilities/urls';

import { TrustpilotReviewsCarousel } from 'Components/reviews/ReviewsCarousel/TrustpilotReviewsCarousel';
import { Content } from 'LegacySBCRA/Content/Content';
import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';

import { CONSENT_CHANGED_EVENT } from '../../CookieBanner/cookieConsent.constants';

import styles from './ReviewsList.scss';

interface TrustPilotReviewsListHtmlProps {
  failedLoad?: boolean | null;
  ref?: React.RefObject<HTMLDivElement | null>;
}

interface ReviewsListProps extends TrustPilotReviewsListHtmlProps {
  setFailedLoad?: (failed?: boolean) => void;
}

const TrustPilotReviewsListHtml = React.memo(
  (
    {
      failedLoad = null,
      ref,
    }: TrustPilotReviewsListHtmlProps,
  ) => {
    if (failedLoad) {
      return null;
    }

    return (
      <Content className={styles.component}>
        <div
          ref={ref}
          className="trustpilot-widget"
          data-businessunit-id="59f1a7fc0000ff0005aeb29e"
          data-locale="en-GB"
          data-style-height="1800px"
          data-style-width="100%"
          data-template-id="539ad60defb9600b94d7df2c"
          data-theme="light"
          id="trustpilot-widget-list"
        >
          <LoadingArrow data-thc-loading-indicator />
        </div>
      </Content>
    );
  },
  ({ failedLoad: previousValue }, { failedLoad: newValue }) => previousValue === newValue,
);

export const ReviewsList: React.FC<ReviewsListProps> = ({ failedLoad, setFailedLoad }) => {
  const $container = useRef<HTMLDivElement>(null);
  const [consentGranted, setConsentGranted] = useState<boolean | null>(null);

  useEffect(() => {
    setConsentGranted(getAdStorageGranted());

    const handleConsentChanged = () => {
      window.location.reload();
    };

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);

    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
    };
  }, []);

  const observerSettings = {
    disconnectOnVisible: true,
    rootMargin: '500px',
    threshold: 0,
  };

  useTrustPilotWidgetLoader({ $container, observerSettings, setFailedLoad, suppressWidget: !consentGranted });

  if (consentGranted === false) {
    return <Content center>
      <TrustpilotReviewsCarousel />
      <br />
      <Button as="a" className="mw-link mw-link-blue" href={TRUSTPILOT_URL} label={T.translate('customerReviewsView.readMoreReviews')} rel="noopener noreferrer" target="_blank" />
    </Content>;
  }

  return <>
    <TrustPilotReviewsListHtml ref={$container} failedLoad={failedLoad} />
    <Content center>
      <a className="mw-link mw-link-blue" href={TRUSTPILOT_URL} rel="noopener noreferrer" target="_blank">{T.translate('customerReviewsView.readMoreReviews')}</a>
    </Content>
  </>;
};
